import { gql } from '@apollo/client';

export const GET_SHL_LEAGUES = gql`
  query GetShlLeagues {
    MDB {
      id
      _SHL_ {
        id
        leagues {
          id
          name
          slug
          sport
          status
          format
          legs
          teamsCount
          playersPerTeam
          registrationFee
          flexibleDates
        }
      }
    }
  }
`;

export const GET_SHL_SPORTS = gql`
  query GetShlSports {
    MDB {
      id
      _SHL_ {
        id
        sports {
          id
          label {
            it
            en
          }
        }
      }
    }
  }
`;

export const GET_SHL_LEAGUE = gql`
  query GetShlLeague($id: ID!) {
    MDB {
      id
      _SHL_ {
        id
        league(id: $id) {
          id
          name
          slug
          sport
          status
          format
          legs
          teamsCount
          playersPerTeam
          registrationFee
          flexibleDates
          tiebreakers
          competitions {
            id
            type
            name
            sport
            format
            legs
            teamsCount
            availability
            settings
            structure {
              name
              phone
              address {
                street
                zip
                city
              }
            }
          }
        }
        teams(leagueId: $id) {
          id
          name
          shortName
          logo
          availability
          registrationFee
          players {
            id
            displayName
            position
          }
        }
        matches(leagueId: $id) {
          id
          competitionId
          groupId
          stage
          stageRound
          homeTeamId
          awayTeamId
          matchdayNumber
          leg
          scheduledAt
          structure {
            name
            phone
            address {
              street
              zip
              city
            }
          }
          status
          result {
            home
            away
            outcome
          }
          notes
        }
      }
    }
  }
`;

export const GET_SHL_LEAGUE_SETTINGS = gql`
  query GetShlLeagueSettings($id: ID!) {
    MDB {
      id
      _SHL_ {
        id
        league(id: $id) {
          id
          name
          slug
          sport
          status
          format
          legs
          teamsCount
          playersPerTeam
          registrationFee
          flexibleDates
          tiebreakers
          competitions {
            id
            type
            name
            sport
            format
            legs
            teamsCount
            availability
            settings
            structure {
              name
              phone
              address {
                street
                zip
                city
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_SHL_LEAGUE_TEAMS = gql`
  query GetShlLeagueTeams($id: ID!) {
    MDB {
      id
      _SHL_ {
        id
        league(id: $id) {
          id
          sport
          teamsCount
          playersPerTeam
          registrationFee
          flexibleDates
          competitions {
            id
            type
            name
            sport
            format
            legs
            teamsCount
            availability
            settings
            structure {
              name
              phone
              address {
                street
                zip
                city
              }
            }
          }
        }
        teams(leagueId: $id) {
          id
          name
          shortName
          logo
          availability
          registrationFee
          players {
            id
            displayName
            position
          }
        }
      }
    }
  }
`;

export const GET_SHL_LEAGUE_CALENDAR = gql`
  query GetShlLeagueCalendar($id: ID!) {
    MDB {
      id
      _SHL_ {
        id
        league(id: $id) {
          id
          name
          sport
          teamsCount
          registrationFee
          flexibleDates
          competitions {
            id
            type
            name
            sport
            format
            legs
            teamsCount
            availability
            settings
            structure {
              name
              phone
              address {
                street
                zip
                city
              }
            }
          }
        }
        teams(leagueId: $id) {
          id
          name
          shortName
          availability
        }
        matches(leagueId: $id) {
          id
          competitionId
          groupId
          stage
          stageRound
          homeTeamId
          awayTeamId
          matchdayNumber
          leg
          scheduledAt
          structure {
            name
            phone
            address {
              street
              zip
              city
            }
          }
          status
          result {
            home
            away
            outcome
          }
          notes
        }
      }
    }
  }
`;

export const GET_SHL_MATCH = gql`
  query GetShlMatch($id: ID!) {
    MDB {
      id
      _SHL_ {
        id
        match(id: $id) {
          id
          leagueId
          competitionId
          groupId
          stage
          stageRound
          homeTeamId
          awayTeamId
          matchdayNumber
          leg
          scheduledAt
          structure {
            name
            phone
            address {
              street
              zip
              city
            }
          }
          status
          result {
            home
            away
            outcome
          }
          events {
            minute
            type
            teamId
            playerId
            assistPlayerId
            notes
          }
          awards {
            bestPlayerId
            bestGoalkeeperId
          }
          notes
        }
      }
    }
  }
`;

export const GET_SHL_STANDINGS = gql`
  query GetShlStandings($leagueId: ID!, $competitionId: ID!) {
    MDB {
      id
      _SHL_ {
        id
        standings(leagueId: $leagueId, competitionId: $competitionId) {
          id
          leagueId
          competitionId
          groupId
          teamId
          sport
          mp
          w
          l
          gw
          gl
          gd
          pts
          updatedAt
        }
      }
    }
  }
`;

export const GET_SHL_PUBLIC_LEAGUE = gql`
  query GetShlPublicLeague($slug: String!) {
    _SHL_publicLeague(slug: $slug) {
      ok
      error {
        message
      }
      league {
        id
        name
        slug
        sport
        format
        legs
        status
        teamsCount
        playersPerTeam
        registrationFee
        flexibleDates
      }
      teams {
        id
        name
        shortName
        logo
        availability
        registrationFee
        players {
          id
        }
      }
      competitions {
        id
        type
        name
        sport
        format
        legs
        teamsCount
        availability
        settings
        structure {
          name
          phone
          address {
            street
            zip
            city
          }
        }
      }
      matches {
        id
        competitionId
        groupId
        stage
        stageRound
        homeTeamId
        awayTeamId
        matchdayNumber
        leg
        scheduledAt
        structure {
          name
          phone
          address {
            street
            zip
            city
          }
        }
        status
        result {
          home
          away
          outcome
        }
        notes
      }
    }
  }
`;

export const GET_SHL_PUBLIC_MATCH = gql`
  query GetShlPublicMatch($slug: String!, $matchId: ID!) {
    _SHL_publicMatch(slug: $slug, matchId: $matchId) {
      ok
      error {
        message
      }
      league {
        id
        name
        slug
        sport
        format
        legs
        status
      }
      competition {
        id
        type
        name
        format
        legs
      }
      teams {
        id
        name
        shortName
        logo
      }
      match {
        id
        competitionId
        groupId
        stage
        stageRound
        matchdayNumber
        leg
        homeTeamId
        awayTeamId
        scheduledAt
        structure {
          name
          phone
          address {
            street
            zip
            city
          }
        }
        status
        result {
          home
          away
          outcome
        }
        notes
      }
    }
  }
`;

export const GET_SHL_PUBLIC_STANDINGS = gql`
  query GetShlPublicStandings($slug: String!, $competitionId: ID!) {
    _SHL_publicStandings(slug: $slug, competitionId: $competitionId) {
      ok
      error {
        message
      }
      standings {
        id
        leagueId
        competitionId
        groupId
        teamId
        sport
        mp
        w
        l
        gw
        gl
        gd
        pts
        updatedAt
      }
    }
  }
`;
